import React                   from 'react';
import { observer }            from 'mobx-react-lite';
import View                    from '@widesk-ui/components/View';
import Button                  from '@widesk-ui/components/Button';
import Popover                 from '@widesk-ui/components/Popover';
import { RightOutlined }       from '@ant-design/icons';
import { DECOR_MODELS_BY_ID }  from '@tokamap/core';
import type { DecorColorSlot } from '@tokamap/core';
import type { DecorItem }      from '@tokamap/core';
import ColorButton             from '@/TokamapEditor/ux/AreaSidebar/ColorButton';
import Field                   from '@/TokamapEditor/ux/AreaSidebar/Field';
import currentEditorStore      from '@/TokamapEditor/stores/currentEditorStore';
import useDesignTokens         from '@/hooks/useDesignTokens';
import decorStore              from '@/TokamapEditor/decor/decorStore';

function slotLabel(slot: DecorColorSlot, index: number, alongsideNamed: boolean): string {
	if (slot.label) return slot.label;
	if (alongsideNamed) return `Couleur ${index + 1}`;
	return index === 0 ? 'Couleur principale' : `Couleur ${index + 1}`;
}

function sameColors(a?: Record<string, string>, b?: Record<string, string>): boolean {
	const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
	return [...keys].every(key => a?.[key] === b?.[key]);
}

const DecorColorFields = observer(({ item }: { item: DecorItem; }) => {
	const tokens = useDesignTokens();
	const [open, setOpen] = React.useState(false);
	void decorStore.loadedModels;
	const slots = currentEditorStore.get?.mapAdapter.getDecorColorSlots(item);
	if (!slots?.length) return null;

	const model = DECOR_MODELS_BY_ID[item.model];
	const unnamed = slots.filter(slot => !slot.label);
	const others = decorStore.items.filter(other => other.id !== item.id && other.model === item.model);
	const differing = others.filter(other => !sameColors(other.colors, item.colors));
	const changed = slots.filter(slot => item.colors?.[slot.id]).length;

	const choose = (slot: DecorColorSlot, color?: string) => {
		const colors = { ...item.colors };
		if (color && color.toLowerCase() !== slot.color.toLowerCase()) colors[slot.id] = color.toLowerCase();
		else delete colors[slot.id];
		decorStore.update(item.id, { colors: Object.keys(colors).length ? colors : undefined });
	};

	const content = (
		<View gap="sm" style={{ width: 280 }}>
			<View size={13} style={{ fontWeight: 600 }}>Couleurs de l'objet</View>

			<View row wrap gap="xs">
				{slots.map(slot => (
					<ColorButton
						key={slot.id}
						label={slotLabel(slot, unnamed.indexOf(slot), unnamed.length < slots.length)}
						value={item.colors?.[slot.id] ?? slot.color}
						clearLabel="Couleur d'origine"
						remember={false}
						onChange={color => choose(slot, color)}
					/>
				))}
			</View>

			<View row>
				<Button size="small" type="link" disabled={!item.colors} onClick={() => decorStore.update(item.id, { colors: undefined })} style={{ padding: 0 }}>
					Couleurs d'origine
				</Button>
			</View>

			{!!differing.length && (
				<View gap={4}>
					<Button size="small" block onClick={() => decorStore.update(differing.map(other => other.id), { colors: item.colors })}>
						Appliquer aux {differing.length} autre{differing.length > 1 ? 's' : ''}
					</Button>
					<View size={12} color={tokens.text.secondary}>
						{differing.length > 1 ? 'Les autres' : 'L\'autre'} « {model?.label} » de la carte {differing.length > 1 ? 'prennent' : 'prend'} ces couleurs.
					</View>
				</View>
			)}
		</View>
	);

	return (
		<Field label="Couleurs">
			<Popover
				trigger="click"
				placement="rightTop"
				open={open}
				onOpenChange={setOpen}
				autoAdjustOverflow={{ shiftY: true }}
				content={content}
			>
				<button
					type="button"
					aria-label="Couleurs de l'objet"
					style={{
						height: 40,
						padding: '0 10px',
						borderRadius: 8,
						cursor: 'pointer',
						display: 'flex',
						alignItems: 'center',
						gap: 10,
						fontSize: 14,
						color: tokens.text.default,
						border: `1px solid ${open ? tokens.color.primary : tokens.border.default}`,
						background: tokens.surface.elevated,
					}}
				>
					<span style={{ display: 'inline-flex' }}>
						{slots.map((slot, index) => (
							<span
								key={slot.id}
								style={{
									width: 18,
									height: 18,
									borderRadius: '50%',
									marginLeft: index ? -5 : 0,
									border: `1px solid ${tokens.border.default}`,
									boxShadow: `0 0 0 1px ${tokens.surface.elevated}`,
									background: item.colors?.[slot.id] ?? slot.color,
								}}
							/>
						))}
					</span>
					<span style={{ flex: 1, textAlign: 'left', color: changed ? tokens.text.default : tokens.text.secondary }}>
						{changed ? `${changed} modifiée${changed > 1 ? 's' : ''}` : "D'origine"}
					</span>
					<RightOutlined style={{ fontSize: 10, color: tokens.text.secondary }} />
				</button>
			</Popover>
		</Field>
	);
});

export default DecorColorFields;
